import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { DateTimeService, UIService } from '@services';

import * as Highcharts from 'highcharts';

import { TranslateService } from '@ngx-translate/core';
import {
  ActiveErrorsStore,
  DevicesStore,
  InstallationDetailsStore,
  TrendingChartType,
  TrendingData,
  TrendingEnumMappedProperties,
  TrendingEnumSeriesData,
  TrendingFeatures,
  TrendingItem,
  TrendingStore,
} from '@store';
import { UnknownPropertyFromBackend } from '@vi-iot/ui/components/tabs/tabs.component';
import { cloneDeep, isEqual } from 'lodash-es';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { TRENDING_UNITS_ADAPTER_IT } from '../../../../../operational-data-common/services/units-adapter/units-adapter.module';
import { UnitsAdapter } from '../../../../../operational-data-common/services/units-adapter/units.adapter';
import { BaseTrendingChartComponent } from '../base-trending-chart.component';
import { TrendingChartLegendComponent } from '../trending-chart-legend/trending-chart-legend.component';
import { initXRangeTrendingChartOptions } from '../trending-chart.helpers';
import { getSeriesInXRangeDataFormat } from './xrange.mapper';

@Component({
  selector: 'app-trending-xrange-chart',
  templateUrl: './trending-xrange-chart.component.html',
  styleUrls: ['./trending-xrange-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrendingXrangeChartComponent extends BaseTrendingChartComponent {
  enumPropertiesMapping: TrendingEnumMappedProperties = {};
  @ViewChild('customLegendContainer', { read: ViewContainerRef })
  customLegend: ViewContainerRef;

  constructor(
    protected trendingStore: TrendingStore,
    protected cdr: ChangeDetectorRef,
    protected translateService: TranslateService,
    protected uiService: UIService,
    protected activeErrorsStore: ActiveErrorsStore,
    protected dateTimeService: DateTimeService,
    protected devicesStore: DevicesStore,
    protected installationDetailsStore: InstallationDetailsStore,
    @Inject(TRENDING_UNITS_ADAPTER_IT) protected unitsAdapter: UnitsAdapter
  ) {
    super(
      trendingStore,
      cdr,
      translateService,
      uiService,
      activeErrorsStore,
      dateTimeService,
      devicesStore,
      installationDetailsStore,
      unitsAdapter
    );

    this.initChartOptions = {
      ...cloneDeep(initXRangeTrendingChartOptions),
    };
    this.maxSelectedProperties = 1;
  }

  protected initChart(): void {
    this.onEnumPropertiesMappingChanged();

    super.initChart();
  }

  protected createChartOptions(data: TrendingData, features: string[]): Highcharts.Options {
    const categories = this.getChartCategories(features);

    return {
      ...this.initChartOptions,
      chart: {
        ...this.initChartOptions.chart,
        events: {
          load: (): void => {
            setTimeout(() => {
              this.generateCustomLegend(this.chart);
            }, 0);
          },
          selection: (event: any): boolean => {
            if (event.resetSelection) {
              this.resetExtremesToDatepickerDefaults();
            }

            return true;
          },
        },
      },
      xAxis: {
        ...this.initChartOptions.xAxis,
        min: data.point_start,
        max: data.point_end,
      },
      yAxis: {
        title: {
          text: '',
        },
        categories,
        max: categories.length - 1,
        zoomEnabled: false,
      },
      plotOptions: {
        series: {
          visible: true,
          events: {
            // happening before state update
            legendItemClick: (e): void => {
              this.onSelectChartProperty(e.target.userOptions.id);
            },
          },
          dataGrouping: {
            enabled: true,
          },
        },
        column: {
          pointInterval: 24 * 3600 * 1000, // one day
        },
      },
      series: this.getChartSeries(data, features),
    };
  }

  protected onSelectChartProperty(property: string): void {
    this.selectedProperties.setValue([property]);
  }

  protected generateSeriesFromFeatures(
    data: TrendingData,
    features: string[]
  ): Highcharts.SeriesOptionsType[] {
    let series = [];

    data.devices.forEach((device) => {
      const deviceSeries = this.featureListWithSeries.map((entry) => {
        const visible = !!features.find((set) => {
          return set === `${device.boilerSerial}_${entry.feature}`;
        });

        return {
          deviceId: device.deviceId,
          boilerSerial: device.boilerSerial,
          deviceType: device.deviceType,
          shortDeviceName: device.shortDeviceName,
          id: `${device.boilerSerial}_${entry.feature}`,
          data: getSeriesInXRangeDataFormat(
            this.getSeriesInXRangeDataFormatArgs(entry, data, device.boilerSerial),
            data.point_end
          ),
          type: TrendingChartType.XRange,
          name: entry.label,
          showInLegend: true,
          visible,
          pointWidth: 20,
          borderColor: 'gray',
        };
      });

      series = series.concat(deviceSeries);
    });

    const seriesWithData = series.filter((entry) => entry.data.length > 0);

    return seriesWithData;
  }

  private getSeriesInXRangeDataFormatArgs(
    entry: TrendingItem,
    data: TrendingData,
    boilerSerial: string
  ): TrendingEnumSeriesData {
    const enumSeriesData = data.enum_series.find((set) => set?.feature === entry.feature);

    if (entry.feature === TrendingFeatures.FourWayCircuitValves) {
      const fourWayCircuitValuesProperties =
        data?.enum_properties[`${boilerSerial}_${TrendingFeatures.FourWayCircuitValves}`];
      if (
        fourWayCircuitValuesProperties &&
        fourWayCircuitValuesProperties[0] === UnknownPropertyFromBackend.unknown
      ) {
        delete fourWayCircuitValuesProperties[0];
      }
      const mappedValue = enumSeriesData.value.map((element) =>
        element === 0 ? null : element ? --element : element
      );

      return { ...enumSeriesData, value: mappedValue };
    }

    return enumSeriesData;
  }

  private getChartCategories(features: string[]): string[] {
    const noPropertyPresent = !features.length;
    if (noPropertyPresent) {
      return [];
    }

    const selectedProperty = features[0];
    const selectedPropertyMapping = this.enumPropertiesMapping[selectedProperty];

    const categories = Object.keys(selectedPropertyMapping).map((enumMapping: string) => {
      return this.translateService.instant(
        `+FEATURE_ENUM_STATUS.${selectedPropertyMapping[enumMapping].toUpperCase()}`
      );
    });

    return categories || [];
  }

  private onEnumPropertiesMappingChanged(): void {
    this.trendingStore.enumPropertiesMapping$
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((propertiesMapping: TrendingEnumMappedProperties) => {
        this.enumPropertiesMapping = propertiesMapping;
      });
  }

  private resetExtremesToDatepickerDefaults(): void {
    setTimeout(() => {
      this.chart.xAxis[0].setExtremes(this.datePickerMin, this.datePickerMax);
      this.chart.update(this.chartOptions, true);
    }, 0);
  }

  private generateCustomLegend(context: Highcharts.Chart): void {
    const newComponentRef = this.customLegend.createComponent(TrendingChartLegendComponent);
    newComponentRef.instance.chart = context;
    newComponentRef.instance.redrawLegend();
  }
}
